"use client";

import { motion } from "framer-motion";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { useCountdown } from "@/lib/utilHooks";
import { usePathname } from "next/navigation";
import {
  resolveI18nRichtext,
  resolveI18nText,
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "@/sanity/globals/I18n";
import { resolvePageLink } from "@/sanity/globals/CTA";
import IQCLogo from "@/components/global/ui/IQCLogo";
import Image from "@/helpers/Image";
import { PageAnniversarySchema } from "@/sanity/pages/PageAnniversaryTemplate";

const ease = [0.25, 0.1, 0.25, 1] as const;

function CountdownUnit({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-[1rem] w-[8rem]">
      <p className="mb-0 text-[6rem] leading-none tracking-[-0.12rem] font-medium tabular-nums">
        {value}
      </p>
      <p className="mb-0 text-[1.75rem] leading-none uppercase tracking-[-0.0875rem] text-white/50">
        {label}
      </p>
    </div>
  );
}

export default function CTASection({ data }: { data: PageAnniversarySchema }) {
  const { days, hours, minutes, seconds } = useCountdown(
    data.countdownDate ?? "",
  );

  const pathname = usePathname();
  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;

  const footer = data.settings?.footer;
  const disclaimerText = resolveI18nRichtext(
    footer?.disclaimer,
    footer?.disclaimerI18n,
    language,
  );

  // Only links from the "POLICY" nav column
  const legalLinks =
    footer?.navColumns?.find(
      (col) =>
        resolveI18nText(col.heading, col.i18n, language)?.toUpperCase() ===
        "POLICY",
    )?.links ?? [];

  if (!data.ctaTitle && !disclaimerText) return null;

  return (
    <>
      {/* ── CTA Section ──────────────────────────────────────────── */}
      {data.ctaTitle && (
        <section className="relative w-full bg-[#f7f7f7] overflow-hidden pt-[10rem] lg:pt-[12rem]">
          {/* Ornamental background */}
          {data.heroBackgroundImage && (
            <div className="absolute inset-0 pointer-events-none z-0">
              <Image
                src={data.heroBackgroundImage}
                alt=""
                className="!w-full object-cover object-top opacity-10 mix-blend-exclusion"
              />
            </div>
          )}

          {/* Centered text + countdown */}
          <div className="relative z-10 flex flex-col items-center text-center px-[3rem] gap-[3rem] lg:gap-[4rem]">
            {data.ctaChip && (
              <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
                {data.ctaChip}
              </p>
            )}

            {data.ctaTitle && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease, delay: 0.1 }}
                className="richtext richtext-center mx-auto prose-headings:font-medium max-sm:prose-headings:text-[5rem] max-sm:prose-headings:tracking-[-0.25rem] prose-headings:mb-[2rem] prose-p:text-[2.25rem]"
              >
                <PortableText
                  value={data.ctaTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {/* Countdown widget */}
            {data.countdownDate && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease, delay: 0.2 }}
                className="relative bg-[#001b14] px-[4rem] pt-[3rem] pb-[4rem] flex flex-col gap-[2rem] items-center w-full max-w-[54.75rem]"
              >
                {/* Green accent bar */}
                <div className="absolute top-[-0.75rem] left-1/2 -translate-x-1/2 h-[0.75rem] w-[41.5rem] bg-[#3b6162]" />

                {data.countdownLabel && (
                  <p className="mb-0 font-geist font-medium text-[1.75rem] text-white/50 uppercase leading-none tracking-[0.05rem] text-center">
                    {data.countdownLabel}
                  </p>
                )}

                <div className="flex items-center gap-[1.625rem] justify-center font-geist font-medium text-white">
                  <CountdownUnit value={days} label="Tage" />
                  <span className="text-[5rem] text-white/40 leading-none tracking-[-0.25rem] pb-[1rem]">
                    :
                  </span>
                  <CountdownUnit value={hours} label="STD" />
                  <span className="text-[5rem] text-white/40 leading-none tracking-[-0.25rem] pb-[1rem]">
                    :
                  </span>
                  <CountdownUnit value={minutes} label="MIN" />
                  <span className="text-[5rem] text-white/40 leading-none tracking-[-0.25rem] pb-[1rem]">
                    :
                  </span>
                  <CountdownUnit value={seconds} label="SEK" />
                </div>

                {data.ctaCTA && (
                  <div className="w-full">
                    <CTAButton data={data.ctaCTA} />
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Statue image with fade */}
          {data.heroImage && (
            <div className="relative mt-[4rem] lg:mt-[6rem] w-full max-w-[71.5rem] mx-auto">
              <Image src={data.heroImage} alt="" className="w-full" />
              <div className="absolute bottom-0 left-0 right-0 h-[22.25rem] bg-gradient-to-t from-[#f4f4f4] to-transparent pointer-events-none" />
            </div>
          )}
        </section>
      )}

      {/* ── Footer Disclaimer ────────────────────────────────────── */}
      {(disclaimerText || !!legalLinks.length) && (
        <section className="relative w-full bg-[#f4f4f4] py-[6rem] px-[3rem] lg:py-[8rem] lg:px-[4rem]">
          <div className="flex flex-col mx-auto gap-[4rem] w-[min(140rem,100%)]">
            {/* Copyright + Logo + Legal links */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-[2rem]">
              <p className="m-0 font-geist text-black/70 text-[1.75rem] lg:text-[2rem] flex-1 max-sm:order-1">
                © IQ Capital - All rights reserved
              </p>
              <div className="flex-shrink-0 ">
                <IQCLogo width="15.5rem" height="2.9rem" fill="#001b14" />
              </div>
              {!!legalLinks.length && (
                <ul className="max-sm:order-2 flex flex-1 flex-wrap items-center justify-center lg:justify-end p-0 m-0 list-none gap-x-[1rem] gap-y-[0.5rem]">
                  {legalLinks.map((item, idx) => {
                    const href = resolvePageLink(item.link, language) ?? "#";
                    const isExternal =
                      item.link?.externalLink && item.link?.openInNewTab;
                    const isLast = idx === legalLinks.length - 1;
                    return (
                      <li
                        key={idx}
                        className="m-0 flex items-center gap-[1rem]"
                      >
                        <a
                          href={href}
                          target={isExternal ? "_blank" : undefined}
                          rel={isExternal ? "noopener noreferrer" : undefined}
                          className="transition-all hover:underline text-black/80 text-[1.75rem] underline-offset-4 font-geist lg:text-[2rem]"
                        >
                          {resolveI18nText(item.title, item.i18n, language)}
                        </a>
                        {!isLast && (
                          <span className="text-black/40 text-[1.75rem] lg:text-[2rem]">
                            /
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="w-full border-t h-[.125rem] border-t-black/10" />

            {disclaimerText && (
              <div className="richtext richtext-center prose-p:text-black/60 prose-p:text-[1.75rem] prose-p:leading-[150%] prose-p:font-geist">
                <PortableText
                  value={disclaimerText}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
